import axios from 'axios'
import type { UserDecathlon } from 'src/components/account/types'

import { axiosClient } from '../../services/axiosInstance'

const BIRTHDAY_MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const

export type DyUserDataPayload = {
  cuid: string
  name?: string
  favorite_sports?: string[]
  favorite_store?: string
  birthday_month?: string
  city?: string
  decathlon_club_tier?: string
  gender?: string
  favorite_products?: string[]
  recent_online_purchases?: string[]
}

export function compact<T extends Record<string, unknown>>(input: T): T {
  return Object.fromEntries(
    Object.entries(input).filter(([, value]) => {
      if (value == null) return false
      if (typeof value === 'string') return value.trim().length > 0
      if (Array.isArray(value)) return value.length > 0

      return true
    })
  ) as T
}

export function birthdayMonthFromBirthdate(birthdate?: string) {
  if (!birthdate) return undefined

  const monthToken = birthdate.includes('/')
    ? birthdate.split('/')[1]
    : birthdate.split('-')[1]

  const monthIndex = Number(monthToken) - 1

  if (Number.isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11) {
    return undefined
  }

  return BIRTHDAY_MONTHS[monthIndex]
}

export function getDyCuid(user?: UserDecathlon | null) {
  const cuid = user?.getUserProfile?.sub?.trim()

  if (!cuid) return undefined

  return cuid
}

export function mapUserDecathlonToDyPayload(
  user: UserDecathlon,
  extras?: Pick<
    DyUserDataPayload,
    'favorite_store' | 'favorite_sports' | 'favorite_products'
  >
): DyUserDataPayload | null {
  const cuid = getDyCuid(user)

  if (!cuid) return null

  const {
    given_name: givenName,
    family_name: familyName,
    gender,
    birthdate,
  } = user.getUserProfile.claims ?? {}

  const name = [givenName, familyName].filter(Boolean).join(' ').trim()
  const favoriteAddress =
    user.getAddresses?.find((address) => address.is_favorite) ??
    user.getAddresses?.[0]

  return compact({
    cuid,
    name,
    gender,
    birthday_month: birthdayMonthFromBirthdate(birthdate),
    city: favoriteAddress?.locality,
    decathlon_club_tier: user.getDecathlonClubStatus?.clubLevel,
    favorite_store: extras?.favorite_store,
    favorite_sports: extras?.favorite_sports,
    favorite_products: extras?.favorite_products,
  })
}

export async function upsertDyUserData(
  payload: (Omit<DyUserDataPayload, 'cuid'> & { cuid?: string }) | null
): Promise<void> {
  const cuid = payload?.cuid?.trim()

  if (!cuid) return

  try {
    await axiosClient.post(
      '/api/account/upsertDyUserData',
      compact({ ...payload, cuid })
    )
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.warn('[dy-userdata] ingest failed', {
        status: error.response?.status,
        code: error.code,
      })

      return
    }

    console.warn('[dy-userdata] ingest failed')
  }
}
